import React from 'react';
import styled from 'styled-components';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { IPriceData, CablingType } from '../../../../Quotes/types/store';
import { currencyFormatter } from '../../../../shared/utils/currencyFormatter';
import {
  setInstallCost,
  setAnnualCost,
  hasCampusCrossConnect,
  extractCampusCrossConnectLocation,
  getCampusCrossConnectValue,
} from '../../../../Quotes/QuoteBuilder/components/Price/components/Cabling/utils/cabling';
import Column from '../../../../shared/components/atoms/Column';

interface Props {
  className?: string;
  selectedPrice: IPriceData;
}

const Cabling: React.FunctionComponent<React.PropsWithChildren<Props>> = (props: Props) => {
  const cablingTooltipId = 'cabling-tooltip';
  const isCopper =
    props.selectedPrice.a_end_cabling_type === CablingType.Copper ||
    props.selectedPrice.b_end_cabling_type === CablingType.Copper;

  const installCostAEnd = setInstallCost(isCopper, props.selectedPrice.a_end_cross_connect_cost);

  const annualCostAEnd = setAnnualCost(isCopper, props.selectedPrice.a_end_cross_connect_cost);

  const installCostBEnd = setInstallCost(isCopper, props.selectedPrice.b_end_cross_connect_cost);

  const annualCostBEnd = setAnnualCost(isCopper, props.selectedPrice.b_end_cross_connect_cost);

  const isCampusCrossConnectAEnd = hasCampusCrossConnect('A', props.selectedPrice.additional_services);
  const isCampusCrossConnectBEnd = hasCampusCrossConnect('B', props.selectedPrice.additional_services);

  const campusCrossConnectAEndValue = getCampusCrossConnectValue('A', 'Price', props.selectedPrice.additional_prices);
  const campusCrossConnectBEndValue = getCampusCrossConnectValue('B', 'Price', props.selectedPrice.additional_prices);

  const renderCablingEnd = (
    cablingType: CablingType | null,
    postCode: string | null,
    installCost: number,
    annualCost: number
  ) => {
    return (
      <>
        {cablingType !== null && (
          <div className="section">
            <div className="row no-gutters">
              <Column classNames={['text-left', 'cabling_label']}>Cabling request for {postCode}</Column>
            </div>

            {installCost > 0 && (
              <div className="row no-gutters">
                <Column defaultWidth={8} classNames={['column-heading', 'text-left']}>
                  Supplier installation charge
                </Column>
                <Column defaultWidth={4} classNames={['text-right']}>
                  {currencyFormatter.format(installCost)}
                </Column>
              </div>
            )}

            {annualCost > 0 && (
              <div className="row no-gutters">
                <Column defaultWidth={8} classNames={['column-heading', 'text-left']}>
                  Supplier annual cost
                </Column>
                <Column defaultWidth={4} classNames={['text-right']}>
                  {currencyFormatter.format(annualCost)}
                </Column>
              </div>
            )}
            <div className="row no-gutters">
              <Column defaultWidth={8} classNames={['column-heading', 'text-left']}>
                Cabling type
              </Column>
              <Column defaultWidth={4} classNames={['text-right cablingType']}>
                {cablingType}
              </Column>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderCampusCrossConnectEnd = (
    hasCampusCrossConnectForEnd: boolean,
    postCode: string | null,
    viaLocation: string | null,
    installCost: number,
    annualCost: number
  ) => {
    return (
      <>
        {hasCampusCrossConnectForEnd && (
          <div className="section">
            <div className="row no-gutters">
              <Column classNames={['text-left', 'cabling_label']}>
                Campus cross connect for {postCode} via {viaLocation}
              </Column>
            </div>

            {installCost > 0 && (
              <div className="row no-gutters">
                <Column defaultWidth={8} classNames={['column-heading', 'text-left']}>
                  Installation charge
                </Column>
                <Column defaultWidth={4} classNames={['text-right']}>
                  {currencyFormatter.format(installCost)}
                </Column>
              </div>
            )}

            {annualCost > 0 && (
              <div className="row no-gutters">
                <Column defaultWidth={8} classNames={['column-heading', 'text-left']}>
                  Annual cost
                </Column>
                <Column defaultWidth={4} classNames={['text-right']}>
                  {currencyFormatter.format(annualCost)}
                </Column>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const tooltipContent = (
    <>
      {isCampusCrossConnectAEnd
        ? renderCampusCrossConnectEnd(
            isCampusCrossConnectAEnd,
            props.selectedPrice.a_end_p_o_p,
            extractCampusCrossConnectLocation('A', props.selectedPrice.additional_services),
            campusCrossConnectAEndValue?.install ?? 0,
            campusCrossConnectAEndValue?.annual ?? 0
          )
        : renderCablingEnd(
            props.selectedPrice.a_end_cabling_type,
            props.selectedPrice.a_end_p_o_p,
            installCostAEnd,
            annualCostAEnd
          )}

      {isCampusCrossConnectBEnd
        ? renderCampusCrossConnectEnd(
            isCampusCrossConnectBEnd,
            props.selectedPrice.b_end_p_o_p,
            extractCampusCrossConnectLocation('B', props.selectedPrice.additional_services),
            campusCrossConnectBEndValue?.install ?? 0,
            campusCrossConnectBEndValue?.annual ?? 0
          )
        : renderCablingEnd(
            props.selectedPrice.b_end_cabling_type,
            props.selectedPrice.b_end_p_o_p,
            installCostBEnd,
            annualCostBEnd
          )}

      <hr />

      <div className="cabling_description text-left">
        Cross connect cabling charges may apply to your order. Neos Networks will pass through the costs quoted by
        the Data Centre operator with an admin charge added per end as required.
      </div>
    </>
  );

  return (
    <div className={props.className}>
      <span data-tooltip-id={cablingTooltipId}>
        Cabling requested
      </span>
      <div className="tooltip-print">
        <ReactTooltip 
          id={cablingTooltipId} 
          className="cabling-contents" 
          place="bottom" 
          variant="light" 
          opacity={1} 
          openOnClick 
          globalCloseEvents={{ clickOutsideAnchor: true }}>
          {tooltipContent}
        </ReactTooltip>
        <div className="tooltip-print-content">{tooltipContent}</div>
      </div>
    </div>
  );
};

const StyledCabling = styled(Cabling)`
  span {
    color: ${(props) => props.theme.colours.primaryB1};
    text-decoration: underline;
    cursor: pointer;
  }

  .cabling_label {
    color: ${(props) => props.theme.colours.secondary};
    font-family: ${(props) => props.theme.typography.fontFamilyBold};
  }

  .cabling_description {
    width: 350px;
  }

  .cablingType {
    text-transform: capitalize;
  }

  .cabling-contents {
    font-size: 1em;
    z-index: 1;
    width: 370px;

    .section + .section {
      padding-top: 1em;
    }
  }

  .tooltip-print-content {
    display: none;
  }

  @media print {
    span[data-tooltip-id='cabling-tooltip'] {
      display: none;
    }

    .tooltip-print-content {
      display: block;
      width: 370px;
      margin: 60px 0 0 -100px;
      background: #fff;
      border: 1px solid #d5dbe0;
      padding: 12px;
    }
  }
`;

export default StyledCabling;
